import type { Routine, RecurrencePattern } from '@/types/actionable'
import { matchesLayers } from '@/lib/today/domainFilter'
import type { Layer } from '@/lib/domains'
import type { AssigneeFilter } from '@/lib/today/types'
import { isWeekendWindowDay, weekendWindowKeys } from '@/lib/cadence/weekendWindow'
import { hasMonthlyPosition, monthlyPositionWindowFor } from '@/lib/cadence/monthlyPosition'

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const

/** Recurrence day keys indexed by JS Date.getDay() (0 = Sunday). */
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/** The recurrence day key ('sun'…'sat') for a given date's weekday. */
export function weekdayKeyForDate(date: Date): string {
  return WEEKDAY_KEYS[date.getDay()]
}

/**
 * Build the routine update for pinning a routine to a specific date+time
 * (e.g. dropped onto the schedule grid). Converts it to a weekly routine on
 * that weekday, preserving any other recurrence fields, and sets the time.
 */
export function scheduleRoutineOnDate(
  routine: Routine,
  date: Date,
  time: string,
): { recurrence_pattern: RecurrencePattern; time_of_day: string } {
  return {
    recurrence_pattern: { ...routine.recurrence_pattern, type: 'weekly', days: [weekdayKeyForDate(date)] },
    time_of_day: time,
  }
}

/**
 * Is this routine a tracked obligation that keeps its row on Today's main list
 * even when it has no time (PT exercises — the same routines "hide daily"
 * never sweeps)? The day plan (lib/today/dayPlan.ts) and the week journal ask
 * this to decide main-list vs pin placement; they never decide VISIBILITY —
 * a routine reaches them only after resolveRoutine said it shows.
 */
export function isTimelineObligation(routine: Pick<Routine, 'pin_to_timeline'>): boolean {
  return !!routine.pin_to_timeline
}

/**
 * Does the routine's rule NAME the days it is due — every day, listed
 * weekdays (Tue/Thu, Sat+Sun), a day of the month or year, or listed dates?
 * Only a rule that leaves the day open does not: the `weekend` window
 * ("either day, once"), `since_last` ("when it's time again"), or a weekly
 * rule with no days yet. A weekly ['sat','sun'] rule is two named days, not
 * the weekend window.
 */
export function namesDueDays(p: RecurrencePattern | null | undefined): boolean {
  // "First weekend of the month" is the weekend window again, once a month:
  // either day, so it names none. "Last Friday" names its day.
  if (hasMonthlyPosition(p)) return p.day_of_week !== 'weekend'
  switch (p?.type) {
    case 'daily':
    case 'monthly':
    case 'quarterly':
    case 'yearly':
    case 'specific_days':
      return true
    case 'weekly':
      return (p.days?.length ?? 0) > 0
    default:
      return false
  }
}

/**
 * On Today by its own settings, on every day it is due (Scott, 2026-09-27):
 * "Show in Today" POSITIVELY on (`show_on_timeline === true`) and a rule that
 * names its due days. Such an occurrence is on Today's main list and the week
 * journal on those days, untimed — no second "Choose for today", no invented
 * time. `null` is "not said", never read as a deliberate choice; `false` is
 * off Today (resolveRoutine hides it before this is asked).
 * Placement only, like isTimelineObligation — except that resolveRoutine's
 * generic hide-daily sweep also yields to it (see there).
 */
export function isDayBoundRoutine(routine: Pick<Routine, 'recurrence_pattern' | 'show_on_timeline'>): boolean {
  return routine.show_on_timeline === true && namesDueDays(routine.recurrence_pattern)
}

/**
 * True when a routine effectively recurs at least every weekday (>= 5x/week).
 * Covers:
 *   - `daily` (7x/week)
 *   - `weekly` whose selected days cover all five weekdays (Mon–Fri),
 *     which is how NL-parsed "weekdays" routines are persisted (`type:'weekly'`
 *     + `days:['mon','tue','wed','thu','fri']`).
 *   - `specific_days` whose days cover all five weekdays (defensive — same
 *     practical frequency).
 *
 * The visibility toggle (Show daily / Hide daily) controls these specifically;
 * lower-frequency routines (weekend-only `weekly`, ordinary `weekly`,
 * `monthly`, `quarterly`, etc.) are always visible.
 */
export function isEverydayRoutine(rp?: RecurrencePattern | null): boolean {
  if (!rp) return false
  if (rp.type === 'daily') return true
  if ((rp.type === 'weekly' || rp.type === 'specific_days') && rp.days) {
    const set = new Set(rp.days.map(d => d.toLowerCase()))
    return WEEKDAYS.every(d => set.has(d))
  }
  return false
}

/**
 * Whether a routine is rare enough to be NEWS on the kitchen wall.
 *
 * The wall shows what is unusual about today. A thing that happens every
 * day, or most days, is the shape of a week — brushing teeth, feeding the
 * dog — and drawing it beside a dentist appointment teaches people to stop
 * reading the wall. Two days a week or fewer is the line (Scott, 2026-09-07):
 * "once or twice a week" is what he named as worth a glance. Anything on a
 * longer cadence — every other week, monthly, since the last time — is by
 * construction rarer than that and always earns its place.
 *
 * Distinct from `isEverydayRoutine`, which asks "does this run Mon–Fri?" for
 * the Show-daily toggle. That meaning stays; this is a stricter question for
 * a different surface.
 */
export function routineEarnsTheWall(rp?: RecurrencePattern | null): boolean {
  if (!rp) return false
  if ((rp.interval ?? 1) > 1) return true
  switch (rp.type) {
    case 'monthly':
    case 'quarterly':
    case 'yearly':
    case 'since_last':
      return true
    case 'daily':
      return false
    case 'weekly':
    case 'specific_days': {
      if (rp.dates && rp.dates.length > 0) return true
      if (!rp.days || rp.days.length === 0) return false
      return rp.days.length <= 2
    }
    default:
      return false
  }
}

/**
 * When a routine actually happens, following a Step up to its collection.
 *
 * A routine collection carries the hour; its Steps carry the order. "Camp
 * Mornings" is 07:00 and `visibility: 'reference'`, and its five Steps —
 * "Wake, brush teeth, get dressed", "Eat breakfast", "Read", "Out the door",
 * "Camp dropoff" — are active with `time_of_day: null`. Read a Step on its own
 * and it looks like a habit with no hour, so every surface that asks "is this
 * still ahead?" has to answer yes forever. On the kitchen wall that put the
 * whole camp-morning checklist on the board at 7:33pm.
 *
 * A collection parent being resting (`visibility: 'reference'`) like this is
 * the normal, unremarkable shape — see `useWallData.ts`'s own comment: "a
 * routine collection is 'reference' (it never renders itself)". On Today,
 * that means `resolveRoutine`'s rung 1 hides the parent outright, and
 * `selectVisibleRoutines`'s retention layer (today/statusMaps.ts) does NOT
 * rescue it — that layer only rescues a parent whose resolve reason is
 * `'everyday'` (rung 7), never `'resting'` (rung 1). So a resting parent's
 * Steps are orphaned: `groupRoutineSteps` never finds the parent row to
 * attach them to, and the whole collection renders nothing on Today. That's
 * expected, not a bug — this function exists for surfaces like the wall that
 * need a routine's hour independent of whether Today would show it at all.
 *
 * One level only: a Step's parent is a collection, and a collection has no
 * parent. Walking further would just be a place for a cycle to hide.
 */
export function effectiveTimeOfDay(
  routine: Pick<Routine, 'id' | 'time_of_day' | 'parent_routine_id'>,
  byId: Map<string, Pick<Routine, 'id' | 'time_of_day'>>,
): string | null {
  if (routine.time_of_day) return routine.time_of_day
  const parentId = routine.parent_routine_id
  if (!parentId || parentId === routine.id) return null
  return byId.get(parentId)?.time_of_day ?? null
}

/**
 * Format a date as YYYY-MM-DD in local timezone.
 */
function formatDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Map of routineId → most recent completion date. Used by 'since_last'
 * recurrence to compute the next due date.
 */
export type LastCompletionMap = Map<string, Date>

/**
 * Check if a routine's recurrence pattern matches a given date.
 * Pure function — no React dependencies.
 *
 * @param lastCompletedAt only required for 'since_last' routines. Pass the
 *        most recent completion timestamp; null/undefined means "never done"
 *        which surfaces the routine immediately.
 */
export function matchesRecurrenceForDate(
  routine: Routine,
  date: Date,
  lastCompletedAt?: Date | null,
): boolean {
  const dayOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][date.getDay()]
  const dayOfMonth = date.getDate()
  const month = date.getMonth() + 1 // 1-12
  const dateStr = formatDateString(date)
  const pattern = routine.recurrence_pattern

  switch (pattern.type) {
    case 'daily': {
      if (pattern.interval && pattern.interval > 1 && pattern.start_date) {
        const startDate = new Date(pattern.start_date)
        const diffTime = date.getTime() - startDate.getTime()
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
        return diffDays >= 0 && diffDays % pattern.interval === 0
      }
      return true
    }
    case 'weekly': {
      if (pattern.interval && pattern.interval > 1 && pattern.start_date) {
        const startDate = new Date(pattern.start_date)
        const diffTime = date.getTime() - startDate.getTime()
        const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7))
        if (diffWeeks < 0 || diffWeeks % pattern.interval !== 0) {
          return false
        }
      }
      return pattern.days?.includes(dayOfWeek) ?? false
    }
    // A window, not a day. Every day of the weekend offers it — and doing it
    // on one of them settles the whole window, so Saturday's job does not ask
    // again on Sunday. The day it was DONE still shows it, ticked; only the
    // other days of that window go quiet.
    case 'weekend': {
      if (!isWeekendWindowDay(date)) return false
      // Every other (or Nth) weekend, counted from the weekend it started on
      // (2026-10-03: "Weed the backyard", every other weekend, done once).
      if (pattern.interval && pattern.interval > 1 && pattern.start_date) {
        const saturdayOf = (d: Date) => {
          const key = weekendWindowKeys(d).find((k) => { const [y, m, dd] = k.split('-').map(Number); return new Date(y, m - 1, dd).getDay() === 6 })
          const [y, m, dd] = (key ?? formatDateString(d)).split('-').map(Number)
          return new Date(y, m - 1, dd)
        }
        const [sy, sm, sd] = pattern.start_date.slice(0, 10).split('-').map(Number)
        const start = new Date(sy, sm - 1, sd)
        const startSat = new Date(start.getFullYear(), start.getMonth(), start.getDate() + ((6 - start.getDay() + 7) % 7))
        const weeks = Math.round((saturdayOf(date).getTime() - startSat.getTime()) / (7 * 86_400_000))
        if (weeks < 0 || weeks % pattern.interval !== 0) return false
      }
      if (!lastCompletedAt) return true
      const doneOn = formatDateString(lastCompletedAt)
      if (doneOn === dateStr) return true
      return !weekendWindowKeys(date).includes(doneOn)
    }
    case 'monthly': {
      // By position ("first weekend", "last Friday"). A weekend position is
      // a window like `weekend` above: done on one of its days, the others
      // go quiet.
      if (hasMonthlyPosition(pattern)) {
        const window = monthlyPositionWindowFor(date, pattern)
        if (!window) return false
        if (window.length === 1 || !lastCompletedAt) return true
        const doneOn = formatDateString(lastCompletedAt)
        if (doneOn === dateStr) return true
        return !window.map(formatDateString).includes(doneOn)
      }
      if (!pattern.day_of_month) return false
      // The editor promises "for months with fewer days, the routine occurs
      // on the last day" — a 31st rule is due Sep 30, not never.
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
      return Math.min(pattern.day_of_month, lastDay) === dayOfMonth
    }
    case 'quarterly': {
      const quarterMonths = [1, 4, 7, 10]
      if (!quarterMonths.includes(month)) return false
      const targetDay = pattern.day_of_month || 1
      return dayOfMonth === targetDay
    }
    case 'yearly': {
      const targetMonth = pattern.month_of_year || 1
      const targetDay = pattern.day_of_month || 1
      return month === targetMonth && dayOfMonth === targetDay
    }
    case 'specific_days':
      return pattern.dates?.includes(dateStr) ?? false
    case 'since_last': {
      // Surface once N units have passed since the last completion (or
      // immediately if never completed). Stays surfaced every day until
      // the next completion resets the timer — the user requested
      // "show until you check it off."
      if (!lastCompletedAt) return true
      const interval = pattern.interval ?? 1
      const unit = pattern.unit ?? 'weeks'
      const due = new Date(lastCompletedAt)
      if (unit === 'days') due.setDate(due.getDate() + interval)
      else if (unit === 'weeks') due.setDate(due.getDate() + interval * 7)
      else if (unit === 'months') due.setMonth(due.getMonth() + interval)
      return startOfDay(date).getTime() >= startOfDay(due).getTime()
    }
    default:
      return false
  }
}

/**
 * Filter routines that match a given date.
 *
 * @param lastCompletionByRoutine optional map for 'since_last' lookups; if
 *        omitted, since_last routines are treated as never-completed (always due).
 */
export function getRoutinesForDatePure(
  routines: Routine[],
  date: Date,
  lastCompletionByRoutine?: LastCompletionMap,
): Routine[] {
  return routines.filter(r =>
    matchesRecurrenceForDate(r, date, lastCompletionByRoutine?.get(r.id) ?? null),
  )
}

/**
 * Why a routine is or is not on screen. The rung that matched IS the reason,
 * which is what lets the board (step B) explain a hidden routine in one line
 * instead of listing everything that might be true of it.
 */
export type RoutineHideReason =
  | 'shows'
  | 'resting'        // rung 1 — visibility !== 'active'
  | 'not-today'      // rung 2 — recurrence doesn't match the date (unless deferred in, or date is null)
  | 'off'            // rung 3 — show_on_timeline === false
  | 'other-domain'   // rung 4 — fails the domain lens
  | 'not-theirs'     // rung 5 — the selected member isn't an owner
  | 'in-collection'  // rung 6 — it's a step; the collection renders it
  | 'everyday'       // rung 7 — swept by "hide daily routines"
  | 'kiosk-off'      // the kiosk only — show_on_kiosk === false (resolveRoutineOnWall)

export interface RoutinePrefs {
  /** The "hide daily routines" toggle (rung 7). */
  hideRoutines: boolean
  /** The checked layers (rung 4). Unsorted is a layer, not a wildcard. */
  layers: ReadonlySet<Layer>
}

export interface ResolveRoutineCtx {
  /** The day being asked about. `null` asks the DATE-AGNOSTIC question —
   *  "is this routine eligible at all?" — which skips rung 2 and only
   *  rung 2. Used by drag POOLS, which offer a routine for placement onto
   *  any of several days and must not filter by a single day's recurrence.
   *  Prefer calling `resolveRoutineEligible` over passing `date: null` here
   *  by hand — `Date` and `null` are mutually assignable, so a single-day
   *  surface that means to pass a real date gets no type error if it
   *  accidentally passes null instead. */
  date: Date | null
  /** null/undefined/[] means "everyone" and skips rung 5. */
  member?: AssigneeFilter
  prefs: RoutinePrefs
  /** Required only for 'since_last' recurrence; see matchesRecurrenceForDate.
   *  Unused when `date` is null — rung 2 is skipped entirely. */
  lastCompletedAt?: Date | null
  /** Routine ids explicitly placed onto `date` by a deferral, regardless of
   *  recurrence. An instance-level override of rung 2: the user put it here,
   *  so the pattern does not get to veto it. Meaningless (and unused) when
   *  `date` is null — there is no single day to have been deferred onto. */
  deferredInto?: ReadonlySet<string>
}

export interface RoutineResolution {
  shows: boolean
  reason: RoutineHideReason
  owners: string[]
}

/**
 * Collapse the three assignment columns into one list. Read-side only — the
 * columns stay as they are, and this is the single place that knows the order
 * of preference.
 */
export function routineOwners(routine: Routine): string[] {
  if (routine.assigned_to_all && routine.assigned_to_all.length > 0) {
    return [...routine.assigned_to_all]
  }
  if (routine.assigned_to) return [routine.assigned_to]
  if (routine.default_assignee) return [routine.default_assignee]
  return []
}

/**
 * A routine that survives the "hide daily routines" sweep. An explicit pin, or
 * a dosed routine (N times per day) — the latter is a tracked obligation like
 * PT exercises, not ambient habit noise.
 */
export function isPinnedToTimeline(routine: Routine): boolean {
  return routine.pin_to_timeline === true || (routine.times_per_day?.length ?? 0) > 0
}

function matchesOwners(owners: string[], selected: AssigneeFilter): boolean {
  const ids: string[] =
    selected == null ? [] : Array.isArray(selected) ? selected.filter(Boolean) : [selected as string]
  if (ids.length === 0) return true // "everyone"
  return ids.some((id) => (id === 'unassigned' ? owners.length === 0 : owners.includes(id)))
}

/**
 * The one rule for "should this routine show?". First match wins; the matching
 * rung is the reason.
 *
 * Rung order runs cheapest-and-most-absolute first, so the reason a user is
 * shown is the most fundamental one true of that routine — a resting routine
 * that also doesn't recur today reads better as 'resting' than 'not-today'.
 *
 * Deliberately NOT here, because neither is a visibility question:
 *   - isDraggable  — planning wants untimed routines only (!time_of_day)
 *   - canHeadline  — the wall's glance-card ranking
 */
export function resolveRoutine(routine: Routine, ctx: ResolveRoutineCtx): RoutineResolution {
  const owners = routineOwners(routine)
  const hide = (reason: RoutineHideReason): RoutineResolution => ({ shows: false, reason, owners })

  if (routine.visibility !== 'active') return hide('resting')
  const isDeferredInToday = ctx.deferredInto?.has(routine.id) ?? false
  if (
    ctx.date !== null &&
    !isDeferredInToday &&
    !matchesRecurrenceForDate(routine, ctx.date, ctx.lastCompletedAt ?? null)
  ) {
    return hide('not-today')
  }
  if (routine.show_on_timeline === false) return hide('off')
  if (!matchesLayers(routine.context, ctx.prefs.layers)) return hide('other-domain')
  if (!matchesOwners(owners, ctx.member)) return hide('not-theirs')
  if (routine.parent_routine_id != null) return hide('in-collection')
  // The generic "hide daily" preference yields, on a real day it is due, to a
  // routine positively set to show in Today (Scott, 2026-09-27): the specific
  // choice wins. With no day (date: null — a planning drag pool) the sweep
  // applies as before. NOTE: the column defaults to true, so a routine saved
  // with the default reads the same as one switched on — see
  // docs/planning/2026-09-27-today-daybound-routines.md.
  const showsWhenDue = ctx.date !== null && routine.show_on_timeline === true
  if (ctx.prefs.hideRoutines && isEverydayRoutine(routine.recurrence_pattern) && !isPinnedToTimeline(routine) && !showsWhenDue) {
    return hide('everyday')
  }
  return { shows: true, reason: 'shows', owners }
}

/**
 * Caller-side, NOT a rung: planning surfaces accept only untimed routines as
 * drag sources. A timed routine is visible but cannot be dragged, which is a
 * different question from whether it shows.
 */
export function isDraggableRoutine(routine: Routine): boolean {
  return routine.time_of_day == null
}

/**
 * The date-agnostic question: is this routine eligible AT ALL, independent
 * of any particular day? Runs the full ladder except rung 2. This is what a
 * drag POOL asks — it offers a routine for placement onto any of several
 * days, so a single day's recurrence must not filter it. Callers should use
 * this rather than passing `date: null` by hand.
 */
export function resolveRoutineEligible(
  routine: Routine,
  ctx: Omit<ResolveRoutineCtx, 'date' | 'deferredInto' | 'lastCompletedAt'>,
): RoutineResolution {
  return resolveRoutine(routine, { ...ctx, date: null })
}

// ── Explaining, not deciding ────────────────────────────────────────────────
//
// Additive helpers for surfaces that SAY where a routine shows (the routine
// panel's "Where it shows", the Routines page chips — lib/routines/explain.ts).
// None of them decides what renders; every renderer still asks resolveRoutine.

/** The one layer the kitchen kiosk reads (useWallData's FAMILY_LAYER). */
export const WALL_LAYERS: ReadonlySet<Layer> = new Set<Layer>(['family'])

/**
 * The kitchen kiosk's routine question, quoted from useWallData's one
 * resolveRoutine call site so an explanation can repeat it word for word:
 * rung 3 and rung 6 are overridden there (the kids' routines use the Today
 * switch as a declutter workaround, and the board collapses Steps itself), the
 * lens is the Family layer only, and the hide-daily sweep never runs.
 *
 * It does NOT replace that call site — the wall keeps its own copy. If the
 * wall's overrides change, change this with them.
 */
export function resolveRoutineOnWall(
  routine: Routine,
  date: Date,
  lastCompletedAt?: Date | null,
): RoutineResolution {
  if (routine.show_on_kiosk === false) return { shows: false, reason: 'kiosk-off', owners: routineOwners(routine) }
  return resolveRoutine(
    { ...routine, show_on_timeline: true, parent_routine_id: null },
    { date, prefs: { hideRoutines: false, layers: WALL_LAYERS }, lastCompletedAt: lastCompletedAt ?? null },
  )
}

/**
 * The routine's own switches, read to label a control or word an
 * explanation — never to decide what renders (resolveRoutine does that).
 *   active      running (not Resting — `visibility`)
 *   off         "Off": hidden from Today and planning (`show_on_timeline`)
 *   placedToday takes a main-list row on a due day without a time: a tracked
 *               obligation or a rule that names its days (see dayPlan.ts)
 */
export function routineSwitches(routine: Routine): { active: boolean; off: boolean; kioskOff: boolean; placedToday: boolean } {
  return {
    active: routine.visibility === 'active',
    off: routine.show_on_timeline === false,
    kioskOff: routine.show_on_kiosk === false,
    placedToday: isTimelineObligation(routine) || isPinnedToTimeline(routine) || isDayBoundRoutine(routine),
  }
}

/** The write that turns "Off" on or off — hidden from Today and planning, or
 *  shown again. Lives here so writers elsewhere never spell the column. */
export function offSwitchPatch(off: boolean): { show_on_timeline: boolean } {
  return { show_on_timeline: !off }
}

/** The write that takes a routine off the kitchen kiosk and the kids' tablets, or puts it back. */
export function kioskSwitchPatch(off: boolean): { show_on_kiosk: boolean } {
  return { show_on_kiosk: !off }
}
